import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@fluentui/react/lib/Icon';
import styled from 'styled-components/macro';
// needed for cssprop to work even though it it not used directly
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as types from 'styled-components/cssprop';
import classNames from 'classnames';
import { hideIf, showIf } from '../../utils/react';
import _ from 'lodash';

export interface TitleIconProps {
    iconName: string;
    onClick?: () => void;
    props?: {
        [name: string]: any;
    };
}

type ExpandToggleCallback = (isExpanded: boolean) => void;

type AnimationDelay = "none" | "normal" | "long";

export type ExpandAccordion = () => void;

export interface AccordionProps {
    title: React.ReactNode | ((isExpanded: boolean) => React.ReactNode);
    titleIcon?: string | TitleIconProps;
    children: React.ReactNode;
    expanderLocation?: "left" | "right";
    className?: string;
    headerClassName?: string;
    isExpandedByDefault?: boolean;
    highlightWhenCollapsed?: boolean;
    hideTitleWhenExpanded?: boolean;
    animationDelay?: AnimationDelay;
    "data-cy"?: string;
    onExpandedToggle?: ExpandToggleCallback;
    setExpandAccordion?: (expandAccordion: ExpandAccordion) => void;
}

export default function Accordion(props: AccordionProps): JSX.Element {
    const [isExpanded, setExpanded] = useState(!!props.isExpandedByDefault);

    const onExpandToggle = useCallback((isExpanded: boolean, onExpandedToggle: ExpandToggleCallback) => {
        setExpanded(!isExpanded);
        if (onExpandedToggle) {
            onExpandedToggle(!isExpanded);
        }
    }, [setExpanded]);

    useEffect(() => {
        setExpanded(!!props.isExpandedByDefault);
    }, [props.isExpandedByDefault])

    const expandAccordion = useCallback(() => {
        setExpanded(true);
    }, [setExpanded]);

    useEffect(() => {
        props.setExpandAccordion?.(expandAccordion);
    }, [expandAccordion, props.setExpandAccordion]);

    let titleIcon: JSX.Element;
    if (props.titleIcon) {
        if (typeof props.titleIcon === 'string' || props.titleIcon instanceof String) {
            const icon = props.titleIcon as string;
            titleIcon = <HeaderIcon
                iconName={icon}
            />
        } else {
            const icon = props.titleIcon as TitleIconProps;
            titleIcon = <HeaderIcon
                data-cy="accordion-header-icon"
                iconName={icon.iconName}
                tabIndex={icon.onClick ? 0 : null}
                onKeyPress={icon.onClick && ((evt) => { icon.onClick(); evt.stopPropagation(); })}
                onClick={icon.onClick && ((evt) => { icon.onClick(); evt.stopPropagation(); })}
                {...icon.props} />
        }
    }

    return (
        <article className={props.className} data-cy={props["data-cy"]}>
            <AccordionItemHeader
                style={hideIf(props.hideTitleWhenExpanded && isExpanded)}
                onClick={(e) => onExpandToggle(isExpanded, props.onExpandedToggle)}
                onKeyPress={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        onExpandToggle(isExpanded, props.onExpandedToggle);
                        e.preventDefault();
                    }
                }}
                className={classNames(
                    props.headerClassName,
                    "accordion-header",
                    !isExpanded && props.highlightWhenCollapsed && "highlight-header",
                    props.expanderLocation === "left" ? "expander-left" : "expander-right",
                    isExpanded ? "expanded" : "collapsed"
                )}
                role="button"
                aria-expanded={isExpanded}
                tabIndex={0}
            >
                {props.expanderLocation === "left" && <Expander iconName="ChevronRight" className="expander-icon" />}
                {titleIcon}
                {props.title && <span css="flex-grow: 1">{_.isFunction(props.title) ? props.title(isExpanded) : props.title}</span>}
                {(!props.expanderLocation || props.expanderLocation === "right") && <Expander iconName="ChevronDown" />}
            </AccordionItemHeader>
            {
                <Content
                    animationDelay={props.animationDelay}
                    className={isExpanded && "expanded accordion-content"}
                >
                    <div>
                        {<Expander
                            tabIndex={0}
                            style={showIf(props.hideTitleWhenExpanded && isExpanded)}
                            role="button"
                            aria-expanded={isExpanded}
                            aria-hidden="false"
                            iconName="ChevronUp"
                            className="accordion-expander"
                            onClick={() => onExpandToggle(isExpanded, props.onExpandedToggle)}
                            onKeyPress={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    onExpandToggle(isExpanded, props.onExpandedToggle);
                                }
                            }}
                        />}
                        {isExpanded && props.children}
                    </div>
                </Content>
            }
        </article>
    );
}

export const HeaderPrimaryItem = styled.span`
    font-size: 1.8rem;
    font-weight: 600;
    color: var(--color-on-surface-link);
    vertical-align: middle;

    >* {
        vertical-align: middle;
    }
`;

export const HeaderDetailItem = styled.span`
    font-size: 1.4rem;
    color: var(--color-surface-dark2);
`;

const HeaderIcon = styled(Icon)`
    margin-right: .8rem;
`;

const Expander = styled(Icon)`
    font-size: 1.6rem;
    color: var(--color-on-surface);
    transition: transform 0.4s linear;
`;

const getAnimationProp = (animationDelay: AnimationDelay): string => {
    if (animationDelay === 'none') {
        return "none";
    }

    return `slide-out ${animationDelay === "long" ? "1.2s" : "0.3s"} ease-out`
}

const Content = styled.div<{ animationDelay: AnimationDelay }>`
    &.expanded {
        overflow-y: auto;

        > div {
            animation: ${({ animationDelay }) => getAnimationProp(animationDelay)};
        }

        /* For high contrast mode */
        border: solid 1px var(--color-surface);

        .accordion-expander {
            padding: 1rem;
            float: right;
            cursor: pointer;
        }
    }

    @keyframes slide-out {
        0% {
            overflow-y: none;
            margin-top: -80%;
        }
        100% {
            overflow-y: auto;
            margin-top: 0;
        }
    }
`;

const AccordionItemHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 0 1.6rem;
    cursor: pointer;
    /* height: 6rem; */
    padding: 1.9rem 2rem 1.9rem 1.6rem;
    font-size: 1.8rem;

    &.expanded ${HeaderPrimaryItem} {
        color: var(--color-on-surface);
    }

    &.accordion-header {
        border: solid 1px var(--color-surface);
    }

    &.accordion-header.highlight-header {
        border: solid 1px var(--color-surface-dark2);
    }

    &* {
        vertical-align: middle;
    }

    &.highlight-header {
        background-color: var(--color-surface-dark2);
        color: var(--color-on-surfacedark);

        ${Expander} {
            color: var(--color-on-surfacedark);
        }
    }

    &.expander-left ${Expander} {
        margin-right: 2rem;
    }

    &.expanded.expander-right ${Expander} {
        transform: rotateX(180deg);
    }

    &.expanded.expander-left ${Expander} {
        transform: rotate(90deg);
    }

    &:focus {
        border: solid 1px green;
    }

`;